/**
 * Service pour la galerie d'images d'un produit et de ses déclinaisons.
 *
 * Fournit des méthodes pour :
 * - listImages / removeImage / reorderImages : galerie d'un produit.
 * - listVariantImages / removeVariantImage / reorderVariantImages : galerie
 *   d'une déclinaison — un tableau vide signifie qu'elle hérite de la
 *   galerie du produit (l'API applique cet héritage à la lecture du produit,
 *   pas ici : ces listes montrent toujours ce qui existe réellement).
 *
 * L'upload multipart suit un pattern différent (voir ProductGalleryPanel.vue,
 * même raison que CampaignMediaPanel.vue : axiosInstance n'est pas utilisé
 * pour les uploads dans ce codebase).
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 * Toutes les réponses de cette API sont enveloppées en { data, message } —
 * chaque méthode ci-dessous dépaquette et retourne directement le payload.
 */
import axiosInstance from '@/services/interceptor.services';
import type { AxiosError } from 'axios';
import type { IProductGalleryImage } from '@/types/product-image.types';

interface IApiEnvelope<T> {
  data: T;
  message: string;
}

interface IGalleryOrderItem {
  id: string;
  displayOrder: number;
}

export const ProductImageService = {
  listImages: async (productId: string) => {
    try {
      const response = await axiosInstance.get<
        IApiEnvelope<IProductGalleryImage[]>
      >(`products/${productId}/images`);
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  removeImage: async (productId: string, mediaId: string) => {
    try {
      const response = await axiosInstance.delete(
        `products/${productId}/images/${mediaId}`,
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  reorderImages: async (productId: string, orderedIds: string[]) => {
    try {
      const data: IGalleryOrderItem[] = orderedIds.map((id, i) => ({
        id,
        displayOrder: i,
      }));
      const response = await axiosInstance.put<
        IApiEnvelope<IProductGalleryImage[]>
      >(`products/${productId}/images/set-order`, { data });
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  listVariantImages: async (productId: string, variantId: string) => {
    try {
      const response = await axiosInstance.get<
        IApiEnvelope<IProductGalleryImage[]>
      >(`products/${productId}/variants/${variantId}/images`);
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  removeVariantImage: async (
    productId: string,
    variantId: string,
    mediaId: string,
  ) => {
    try {
      const response = await axiosInstance.delete(
        `products/${productId}/variants/${variantId}/images/${mediaId}`,
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  reorderVariantImages: async (
    productId: string,
    variantId: string,
    orderedIds: string[],
  ) => {
    try {
      const data: IGalleryOrderItem[] = orderedIds.map((id, i) => ({
        id,
        displayOrder: i,
      }));
      const response = await axiosInstance.put<
        IApiEnvelope<IProductGalleryImage[]>
      >(`products/${productId}/variants/${variantId}/images/set-order`, {
        data,
      });
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },
};
